import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Snackbar, Alert, TablePagination, TableSortLabel, Select, MenuItem, FormControl, ButtonBase,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Autocomplete,
    Badge, IconButton, InputLabel, Divider
} from '@mui/material'
import { Search as SearchIcon, BlockOutlined, CheckCircleOutline, PersonAddAlt, PersonRemoveAlt1, DeleteOutline, FilterList } from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, numberWithCommas } from '../../helpers'
import { getCurrencyByPhone } from '../../helpers/countries'
import { Connect, mapStateToProps } from '../../Redux'
import { Search, SearchIconWrapper, StyledInputBase } from '../Home'
import eventBus from '../../configs/eventsBus'
import _ from 'lodash'

interface AppUser {
    _id: string
    Username: string
    Phone: string
    role: 'owner' | 'admin' | 'Super Admin'
    status: 'Active' | 'Suspended' | 'InActive' | 'Deleted'
    balance: number
    earning: number
    totalEarning: number
    totalGamesPlayed: number
    country: string
    dateJoined: string
}

interface UserOption {
    _id: string
    Username: string
    Phone: string
}

interface Props {
    User?: { role?: string }
}

const statusOptions = ['Active', 'Suspended', 'InActive', 'Deleted']
const statusStyles: Record<string, { bg: string, color: string }> = {
    Active: { bg: 'rgba(0,107,70,0.1)', color: '#006B46' },
    Suspended: { bg: 'rgba(211,47,47,0.1)', color: '#d32f2f' },
    InActive: { bg: 'rgba(0,0,0,0.06)', color: '#666' },
    Deleted: { bg: 'rgba(0,0,0,0.08)', color: '#333' }
}

interface StateProps {
    users: AppUser[]
    loading: boolean
    searchInput: string
    searchVal: string
    error: string | null
    page: number
    rowsPerPage: number
    matchedCount: number
    totalUsers: number
    sortOrder: 'asc' | 'desc'
    country: string
    countries: string[]
    status: string
    showFilters: boolean
    showAddAdmin: boolean
    userOptions: UserOption[]
    usersLoading: boolean
    selectedUser: UserOption | null
    promoting: boolean
}

const headers = ['Username', 'Phone', 'Role', 'Status', 'Balance', 'Current Earning', 'Total Earnings', 'Games Played']

const AllUsers: FC<Props> = ({ User }) => {
    const [state, setState] = useState<StateProps>({
        users: [],
        loading: true,
        searchInput: '',
        searchVal: '',
        error: null,
        page: 0,
        rowsPerPage: 20,
        matchedCount: 0,
        totalUsers: 0,
        sortOrder: 'desc',
        country: '',
        countries: [],
        status: '',
        showFilters: false,
        showAddAdmin: false,
        userOptions: [],
        usersLoading: false,
        selectedUser: null,
        promoting: false
    })
    const {
        users, loading, searchInput, searchVal, error, page, rowsPerPage, matchedCount, totalUsers,
        sortOrder, country, countries, status, showFilters, showAddAdmin, userOptions, usersLoading, selectedUser, promoting
    } = state
    const isSuperAdmin = User?.role === 'Super Admin'
    const activeFilterCount = (country ? 1 : 0) + (status ? 1 : 0)

    const searchUsers = (value: string) => {
        if (!value) return setState(s => ({ ...s, userOptions: [], usersLoading: false }))
        setState(s => ({ ...s, usersLoading: true }))
        Api({ controller: 'Users', action: 'searchUser', value })
            .then((resp: any) => setState(s => ({ ...s, userOptions: resp || [], usersLoading: false })))
            .catch(() => setState(s => ({ ...s, usersLoading: false })))
    }
    const debouncedSearchUsers = useCallback(_.debounce(searchUsers, 500), [])

    const promoteToAdmin = () => {
        if (!selectedUser) return
        setState(s => ({ ...s, promoting: true }))
        Api({ controller: 'Users', action: 'promoteToAdmin', targetUserId: selectedUser._id })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, promoting: false, error: resp.error.message || resp.error }))
                else {
                    setState(s => ({ ...s, promoting: false, showAddAdmin: false, selectedUser: null, userOptions: [] }))
                    getAllUsers()
                }
            })
            .catch((err) => setState(s => ({ ...s, promoting: false, error: err?.message || 'Something went wrong.' })))
    }

    const demoteAdmin = (targetUser: AppUser) => {
        eventBus.dispatch('showAlert', {
            title: 'Remove Admin',
            message: `Remove admin access from ${targetUser.Username}? They will go back to a regular user account.`,
            buttons: [
                { text: 'Cancel', backgroundColor: '#DCDCDC', color: '#000' },
                {
                    text: 'Remove',
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    onPress: () => {
                        Api({ controller: 'Users', action: 'demoteAdmin', targetUserId: targetUser._id })
                            .then((resp: any) => {
                                if (resp?.error)
                                    setState(s => ({ ...s, error: resp.error.message || resp.error }))
                                else
                                    getAllUsers()
                            })
                            .catch((err) => {
                                setState(s => ({ ...s, error: err?.message || 'Something went wrong. Please try again.' }))
                            })
                    }
                }
            ]
        })
    }

    useEffect(() => {
        Api({ controller: 'Users', action: 'getUserCountries' })
            .then((resp: any) => setState(s => ({ ...s, countries: Array.isArray(resp) ? resp : [] })))
            .catch(() => { })
    }, [])

    const getAllUsers = () => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'Users',
            action: 'getAllUsers',
            page,
            limit: rowsPerPage,
            sortOrder,
            searchVal,
            country,
            status
        }).then((resp: any) => {
            if (resp?.error)
                setState(s => ({ ...s, loading: false, error: resp.error }))
            else
                setState(s => ({
                    ...s,
                    users: resp.users,
                    matchedCount: resp.matchedCount,
                    totalUsers: resp.totalUsers,
                    loading: false
                }))
        }).catch((err) => {
            setState(s => ({ ...s, loading: false, error: err?.message || 'Something went wrong. Please try again.' }))
        })
    }

    useEffect(getAllUsers, [page, rowsPerPage, sortOrder, searchVal, country, status])

    // Was previously keyed off `status !== 'Suspended'`, which only offered a way back to Active
    // from Suspended - an InActive (unverified signup) or Deleted (self-deleted) account had no
    // path to Active from here, since both would show "Suspend" instead. The server's
    // updateUserStatus already sets status: 'Active' unconditionally when suspend is false,
    // regardless of the account's current status - this was purely a frontend gap.
    const toggleUserStatus = (targetUser: AppUser) => {
        const suspend = targetUser.status === 'Active'
        eventBus.dispatch('showAlert', {
            title: suspend ? 'Suspend User' : 'Activate User',
            message: suspend
                ? `Are you sure you want to suspend ${targetUser.Username}? They will not be able to access their account.`
                : `Activate ${targetUser.Username}'s account? This also clears their wrong-PIN attempt count.`,
            buttons: [
                { text: 'Cancel', backgroundColor: '#DCDCDC', color: '#000' },
                {
                    text: suspend ? 'Suspend' : 'Activate',
                    backgroundColor: suspend ? '#d32f2f' : '#006B46',
                    color: '#fff',
                    onPress: () => {
                        Api({
                            controller: 'Users',
                            action: 'updateUserStatus',
                            targetUserId: targetUser._id,
                            suspend
                        }).then((resp: any) => {
                            if (resp?.error)
                                setState(s => ({ ...s, error: resp.error }))
                            else
                                getAllUsers()
                        }).catch((err) => {
                            setState(s => ({ ...s, error: err?.message || 'Something went wrong. Please try again.' }))
                        })
                    }
                }
            ]
        })
    }

    // Soft-delete (status: 'Deleted') rather than a real removal - every historical row
    // referencing this user's _id (Payments, GadgetOrders, ...) stays intact. Server also blocks
    // removing yourself or a Super Admin, but the status/role checks below keep the button from
    // even showing up for those rows in the first place.
    const removeUser = (targetUser: AppUser) => {
        eventBus.dispatch('showAlert', {
            title: 'Remove User',
            message: `Remove ${targetUser.Username}'s account? They will no longer be able to sign in.`,
            warning: 'This cannot be undone from here.',
            buttons: [
                { text: 'Cancel', backgroundColor: '#DCDCDC', color: '#000' },
                {
                    text: 'Remove',
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    onPress: () => {
                        Api({ controller: 'Users', action: 'removeUser', targetUserId: targetUser._id })
                            .then((resp: any) => {
                                if (resp?.error)
                                    setState(s => ({ ...s, error: resp.error.message || resp.error }))
                                else
                                    getAllUsers()
                            })
                            .catch((err) => {
                                setState(s => ({ ...s, error: err?.message || 'Something went wrong. Please try again.' }))
                            })
                    }
                }
            ]
        })
    }

    // searchInput updates the text field instantly; searchVal (debounced) is what actually
    // drives the fetch above, so typing doesn't fire a request per keystroke.
    const commitSearch = useCallback(_.debounce((val: string) => {
        setState(s => ({ ...s, searchVal: val, page: 0 }))
    }, 700), [])

    const toggleSort = () => setState(s => ({ ...s, sortOrder: s.sortOrder === 'asc' ? 'desc' : 'asc', page: 0 }))

    return (
        <Box>
            <Box sx={{ padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>All Users</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        bgcolor: 'rgba(0,107,70,0.1)', color: 'primary.main', fontWeight: 700,
                        borderRadius: 5, px: 1.8, py: 0.6, fontSize: 14, whiteSpace: 'nowrap'
                    }}>
                        {numberWithCommas(totalUsers)} total users
                    </Box>
                    {isSuperAdmin && (
                        <Button
                            variant='contained'
                            startIcon={<PersonAddAlt />}
                            onClick={() => setState(s => ({ ...s, showAddAdmin: true }))}
                            sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                        >
                            Add Admin
                        </Button>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', px: '5px' }}>
                <Search sx={{ borderRadius: 3, flex: 1, margin: 0 }}>
                    <SearchIconWrapper>
                        <SearchIcon sx={{ fontSize: 20, color: 'black' }} />
                    </SearchIconWrapper>
                    <StyledInputBase
                        placeholder='Search by username or phone'
                        inputProps={{ 'aria-label': 'search' }}
                        value={searchInput}
                        onChange={(e) => {
                            setState(s => ({ ...s, searchInput: e.target.value }))
                            commitSearch(e.target.value)
                        }}
                    />
                </Search>
                <IconButton
                    onClick={() => setState(s => ({ ...s, showFilters: true }))}
                    sx={{ ml: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid rgba(0,0,0,0.15)' }}
                >
                    <Badge color='primary' variant='dot' invisible={!activeFilterCount}>
                        <FilterList sx={{ fontSize: 20 }} />
                    </Badge>
                </IconButton>
            </Box>

            <Dialog
                open={showFilters}
                onClose={() => setState(s => ({ ...s, showFilters: false }))}
                fullWidth
                maxWidth='xs'
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    Filters
                    <Button size='small' disabled={!activeFilterCount} onClick={() => setState(s => ({ ...s, country: '', status: '', page: 0 }))}>
                        Clear all
                    </Button>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '20px !important' }}>
                    <FormControl size='small' fullWidth>
                        <InputLabel>Country</InputLabel>
                        <Select
                            label='Country'
                            value={country}
                            onChange={(e) => setState(s => ({ ...s, country: e.target.value, page: 0 }))}
                        >
                            <MenuItem value=''>All Countries</MenuItem>
                            {countries.map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size='small' fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label='Status'
                            value={status}
                            onChange={(e) => setState(s => ({ ...s, status: e.target.value, page: 0 }))}
                        >
                            <MenuItem value=''>All Statuses</MenuItem>
                            {statusOptions.map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant='contained' fullWidth onClick={() => setState(s => ({ ...s, showFilters: false }))}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
            <Show>
                <Show.When isTrue={loading || !users.length}>
                    <EmptyView
                        loading={loading}
                        oopTxt='No Users'
                        text='No users found.'
                    />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 2, mx: '5px' }}>
                        <Table size='small'>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        <TableSortLabel
                                            active
                                            direction={sortOrder}
                                            onClick={toggleSort}
                                            sx={{
                                                color: 'white !important',
                                                '& .MuiTableSortLabel-icon': { color: 'white !important' }
                                            }}
                                        >
                                            Date Joined
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => {
                                    const { _id, Username, Phone, role, status, balance, earning, totalEarning, totalGamesPlayed, dateJoined } = user
                                    return (
                                        <TableRow key={_id} hover>
                                            <TableCell>{Username}</TableCell>
                                            <TableCell>{Phone}</TableCell>
                                            <TableCell>
                                                {role === 'owner' ? (
                                                    <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>Owner</Typography>
                                                ) : (
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                                                        <Box sx={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            bgcolor: role === 'Super Admin' ? 'rgba(245,197,24,0.15)' : 'rgba(0,107,70,0.1)',
                                                            color: role === 'Super Admin' ? '#8a6d00' : 'primary.main',
                                                            px: 1, py: 0.25, borderRadius: 4, fontSize: 11.5, fontWeight: 700
                                                        }}>
                                                            {role === 'Super Admin' ? 'Super Admin' : 'Admin'}
                                                        </Box>
                                                        {/* Only a plain admin can be demoted here - a Super Admin can't remove
                                                            another Super Admin's role this way (server rejects it too). */}
                                                        {isSuperAdmin && role === 'admin' && (
                                                            <ButtonBase
                                                                onClick={() => demoteAdmin(user)}
                                                                title='Remove admin'
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                    width: 18, height: 18, borderRadius: '50%', color: '#d32f2f',
                                                                    '&:hover': { bgcolor: 'rgba(211,47,47,0.12)' }
                                                                }}
                                                            >
                                                                <PersonRemoveAlt1 sx={{ fontSize: 12 }} />
                                                            </ButtonBase>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                    bgcolor: (statusStyles[status] || statusStyles.InActive).bg,
                                                    color: (statusStyles[status] || statusStyles.InActive).color,
                                                    px: 1, py: 0.3, borderRadius: 5, fontSize: 11.5, fontWeight: 700
                                                }}>
                                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' }} />
                                                    {status}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{getCurrencyByPhone(Phone)} {numberWithCommas(balance)}</TableCell>
                                            <TableCell>{getCurrencyByPhone(Phone)} {numberWithCommas(earning)}</TableCell>
                                            <TableCell>{getCurrencyByPhone(Phone)} {numberWithCommas(totalEarning)}</TableCell>
                                            <TableCell>{totalGamesPlayed}</TableCell>
                                            <TableCell>{new Date(dateJoined).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <ButtonBase
                                                        onClick={() => toggleUserStatus(user)}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                                            px: 1.2, py: 0.4, borderRadius: 5, fontSize: 11.5, fontWeight: 700,
                                                            whiteSpace: 'nowrap',
                                                            bgcolor: status !== 'Active' ? 'rgba(0,107,70,0.1)' : 'rgba(211,47,47,0.1)',
                                                            color: status !== 'Active' ? '#006B46' : '#d32f2f',
                                                            transition: 'background-color 0.15s',
                                                            '&:hover': {
                                                                bgcolor: status !== 'Active' ? 'rgba(0,107,70,0.18)' : 'rgba(211,47,47,0.18)'
                                                            }
                                                        }}
                                                    >
                                                        {status !== 'Active'
                                                            ? <CheckCircleOutline sx={{ fontSize: 13.5 }} />
                                                            : <BlockOutlined sx={{ fontSize: 13.5 }} />}
                                                        {status !== 'Active' ? 'Activate' : 'Suspend'}
                                                    </ButtonBase>
                                                    {/* Hidden for an already-removed account and for a Super Admin target -
                                                        the server blocks the latter too, but there's no point showing a
                                                        button here that can only ever come back as an error. */}
                                                    {role !== 'Super Admin' && status !== 'Deleted' && (
                                                        <IconButton
                                                            size='small'
                                                            title='Remove user'
                                                            onClick={() => removeUser(user)}
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <DeleteOutline sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component='div'
                            count={matchedCount}
                            page={page}
                            onPageChange={(_e, newPage) => setState(s => ({ ...s, page: newPage }))}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => setState(s => ({ ...s, rowsPerPage: parseInt(e.target.value), page: 0 }))}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                        />
                    </TableContainer>
                </Show.Else>
            </Show>

            {isSuperAdmin && (
                <Dialog
                    open={showAddAdmin}
                    onClose={() => !promoting && setState(s => ({ ...s, showAddAdmin: false, selectedUser: null, userOptions: [] }))}
                    fullWidth
                    maxWidth='sm'
                >
                    <DialogTitle sx={{ fontWeight: 'bold' }}>Add Admin</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                        <Autocomplete
                            options={userOptions}
                            getOptionLabel={(o) => o.Username}
                            loading={usersLoading}
                            value={selectedUser}
                            onChange={(_, val) => setState(s => ({ ...s, selectedUser: val }))}
                            onInputChange={(_, val) => debouncedSearchUsers(val)}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            noOptionsText='Type a username or phone number'
                            renderOption={(props, o) => (
                                <li {...props} key={o._id}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.Username}</Typography>
                                        <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.Phone}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Search User'
                                    placeholder='Type a username or phone number...'
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                        {selectedUser && (
                            <Typography sx={{ fontSize: 13, color: '#6b7280', mt: -1 }}>
                                Selected: {selectedUser.Username} · {selectedUser.Phone}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setState(s => ({ ...s, showAddAdmin: false, selectedUser: null, userOptions: [] }))} disabled={promoting}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={promoteToAdmin}
                            disabled={!selectedUser || promoting}
                            startIcon={promoting ? <CircularProgress size={16} color='inherit' /> : undefined}
                        >
                            Make Admin
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(AllUsers)
